import type { FieldValues, Resolver } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import type { ISchema, ObjectSchema } from 'yup';

/**
 * `yupResolver` typed to the form's field-values type.
 *
 * yup 1 infers schema fields as optional (`yup.string()` → `string | undefined`)
 * and our hand-written form types intentionally describe a richer/required shape
 * (extra fields, branded option types, etc.), so the resolver's inferred generics
 * never line up with `useForm<FormType>`. The single, unavoidable bridging cast is
 * centralized here instead of scattering `as unknown as Resolver<…>` across forms.
 *
 * The parameter is typed as the public `ISchema` because yup's `ObjectSchema` is
 * invariant in its shape generic, so concrete/nullable/union object schemas are not
 * assignable to `ObjectSchema<any, …>` — `ISchema<any>` accepts them all.
 *
 * Usage: `resolver: formResolver(schema)`
 */
export const formResolver = <T extends FieldValues>(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    schema: ISchema<any>,
): Resolver<T> =>
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    yupResolver(schema as ObjectSchema<any, any, any, any>) as Resolver<T>;
